import { MenuItem } from "@mui/material";
import * as React from "react";

import { Menu } from "@/shared/components/Menu";
import {
  formatTimeByMode,
  TIME_DISPLAY_MODES,
  TIME_DISPLAY_MODE_LABELS,
  TimeDisplayMode,
} from "@/shared/components/visualization/WorkspaceCtx";
import { ArrowDropDownIcon } from "@/shared/foundation/icons/mui";
import { usePopupState } from "@/shared/hooks";

import styles from "./TimeDisplayField.module.css";

interface TimeModePickerProps {
  mode: TimeDisplayMode;
  currentTimeNs: bigint;
  earliestTimeNs: bigint | null;
  onModeChange: (mode: TimeDisplayMode) => void;
}

export function TimeModePicker({
  mode,
  currentTimeNs,
  earliestTimeNs,
  onModeChange,
}: TimeModePickerProps) {
  const buttonRef = React.useRef<HTMLButtonElement | null>(null);
  const popup = usePopupState();

  const handleSelect = (newMode: TimeDisplayMode) => {
    onModeChange(newMode);
    popup.close();
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={styles.modeButton}
        aria-label="Time display mode"
        aria-haspopup="menu"
        aria-expanded={popup.isOpen}
        onClick={() => {
          if (buttonRef.current) {
            popup.open({ anchorEl: buttonRef.current });
          }
        }}
      >
        <span>{TIME_DISPLAY_MODE_LABELS[mode]}</span>
        <ArrowDropDownIcon fontSize="small" />
      </button>
      <Menu
        anchorEl={popup.anchor}
        open={popup.isOpen}
        onClose={popup.close}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        // StyledMenu's `& .MuiPaper-root` rule sets marginTop: 8px for the
        // default downward opening; we're flipping upward, so override with
        // higher specificity to put the gap above the trigger instead.
        slotProps={{
          paper: {
            sx: {
              "&&.MuiPaper-root": { marginTop: 0, marginBottom: 1 },
            },
          },
        }}
      >
        {TIME_DISPLAY_MODES.map((m) => (
          <MenuItem
            key={m}
            selected={m === mode}
            onClick={() => handleSelect(m)}
            className={styles.menuItem}
          >
            <span className={styles.menuItemMode}>
              {TIME_DISPLAY_MODE_LABELS[m]}
            </span>
            <span className={styles.menuItemTime}>
              {formatTimeByMode(currentTimeNs, m, earliestTimeNs)}
            </span>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
